import React, { forwardRef } from 'react';

/**
 * CustomInput component renders a custom input field.
 * @param {object} props - Component props.
 * @param {string} props.label - The label for the input field.
 * @param {string} props.id - The ID of the input field.
 * @param {string} props.type - The type of the input field.
 * @param {function} props.onChange - The function to call when the input value changes.
 * @param {string} props.value - The value of the input field.
 * @param {string} props.placeholder - The placeholder text for the input field.
 * @param {string} props.name - The name of the input field.
 * @param {string} props.classname - Additional CSS classes for styling the input field.
 * @param {node} props.appEnd - The element to append to the input field.
 * @param {boolean} props.isdisabled - Whether the input field is disabled.
 * @param {number} props.min - The minimum value for numeric input fields.
 * @param {number} props.max - The maximum value for numeric input fields.
 * @param {string} props.error - The error message to display, if any.
 * @param {boolean} props.readOnly - Whether the input field is read-only.
 * @param {number} props.maxlength - The maximum length of the input value.
 * @param {string} props.pattern - The pattern the input value must match.
 * @param {string} props.defaultValue - The default value of the input field.
 * @param {string} props.labelText - Additional text to display next to the label.
 * @param {node} props.icon - The icon to display next to the input field.
 * @param {string} props.src - The source of the icon image.
 * @param {function} props.onClick - The function to call when the icon is clicked.
 * @param {string} props.parentClass - Additional CSS class for the parent container.
 * @param {string} props.labelClass - Additional CSS class for the label.
 * @param {string} props.boxParentClass - Additional CSS class for the input box parent.
 * @param {node} props.prePend - The element to prepend to the input field.
 * @param {function} props.onFocus - The function to call when the input field gains focus.
 * @param {function} props.onBlur - The function to call when the input field loses focus.
 * @param {function} props.onCopy - The function to call when content is copied to the input field.
 * @param {function} props.onPaste - The function to call when content is pasted into the input field.
 * @param {function} props.onCut - The function to call when content is cut from the input field.
 * @param {number} props.tabIndex - The tab index of the input field.
 * @param {ref} ref - The reference to the input field.
 * @returns {JSX.Element} Custom input field component.
 */
const CustomInput = forwardRef((props, ref) => {
    const {
        label,
        id,
        type,
        onChange,
        value,
        placeholder,
        name,
        classname,
        appEnd,
        isdisabled,
        min,
        max,
        error,
        readOnly,
        maxlength,
        pattern,
        labelText,
        icon,
        src,
        onClick,
        parentClass,
        labelClass,
        boxParentClass,
        prePend,
        onFocus,
        onBlur,
        onCopy,
        onPaste,
        onCut,
        tabIndex,
        onInput
    } = props;

    return (
        <div className={`form-group position-relative mb-2 ${parentClass}`}>
            <div className={`position-relative ${boxParentClass}`}>
                {prePend && <div className="prepend_icon">{prePend}</div>}
                <input
                    ref={ref} // Ref attached here
                    type={type}
                    className={`custom_input ${classname}`}
                    name={name}
                    placeholder={placeholder}
                    id={id}
                    defaultValue={value || ''} // Use defaultValue instead of value
                    onChange={onChange} // Add onChange handler
                    onFocus={onFocus}
                    onBlur={onBlur}
                    isdisabled={isdisabled}
                    min={min}
                    max={max}
                    disabled={readOnly}
                    maxLength={maxlength}
                    pattern={pattern}
                    readOnly={readOnly}
                    onInput={onInput}
                    onCopy={onCopy}
                    onPaste={onPaste}
                    onCut={onCut}
                    tabIndex={tabIndex}
                />
                {label && (
                    <label htmlFor={id} className={`custom_lable ${labelClass}`}>
                        {label} {labelText && <span className="labelDesc">({labelText})</span>}
                    </label>
                )}
                {appEnd && <div className="img_eye_icon">{appEnd}</div>}
                {icon && <img src={src} alt="" className="eye-open" onClick={onClick} />}
            </div>
            {error && <p className="error mt-2"> {error}</p>}
        </div>
    );
});

export default CustomInput;